import { keepPreviousData } from "@tanstack/react-query";

/**
 * Defaults for operator/government dashboard server-list queries.
 * Keeps cached data fresh enough for stats while avoiding refetch on every tab
 * switch or window focus when data is already loaded.
 *
 * Jurisdiction, license type, and region filters are part of these query keys.
 * `placeholderData` keeps the previous rows and stats mounted while the new
 * key loads, the same way a column filter refetch does. Without it, `isLoading`
 * flips true and the portal replaces the whole page with a loading screen.
 */
export const DASHBOARD_LIST_STALE_MS = 5 * 60 * 1000;

export const dashboardListQueryOptions = {
  staleTime: DASHBOARD_LIST_STALE_MS,
  refetchOnWindowFocus: false,
  placeholderData: keepPreviousData,
} as const;

/**
 * Use for hooks that run outside the tabbed dashboard (setup, question config,
 * filter options) so jurisdiction shell data is loaded. This section does not
 * stream registrations, unregistered properties, inspections, or remittance.
 */
export const DASHBOARD_STATS_TAB_PRELOAD_ALL = "jurisdictions";

const JURISDICTION_ORDINANCE_SECTIONS = [
  "jurisdictions",
  "ordinances",
] as const;

/** Do not fetch license stats — unused on these sections. */
export const DASHBOARD_SECTIONS_SKIP_LICENSE_STATS = new Set<string>([
  "unregistered-properties",
  "cases",
  "forecasting",
  ...JURISDICTION_ORDINANCE_SECTIONS,
]);

/** @deprecated Use DASHBOARD_SECTIONS_SKIP_LICENSE_STATS. */
export const DASHBOARD_SECTIONS_SKIP_LICENSES =
  DASHBOARD_SECTIONS_SKIP_LICENSE_STATS;

export const DASHBOARD_SECTIONS_SKIP_UNREGISTERED = new Set<string>([
  "registrations",
  "inactive-registrations",
  "forecasting",
  ...JURISDICTION_ORDINANCE_SECTIONS,
]);

export const DASHBOARD_SECTIONS_SKIP_INSPECTIONS = new Set<string>([
  "forecasting",
  ...JURISDICTION_ORDINANCE_SECTIONS,
]);

export const DASHBOARD_SECTIONS_SKIP_REMITTANCE = new Set<string>([
  "forecasting",
  ...JURISDICTION_ORDINANCE_SECTIONS,
]);
